import { __decorate } from "tslib";
import { AutoEncoder, BooleanDecoder, DateDecoder, EmailDecoder, EnumDecoder, field, MapDecoder, StringDecoder } from '@simonbackx/simple-encoding';
import { v4 as uuidv4 } from 'uuid';
import { Permissions } from './Permissions.js';
import { UserPermissions } from './UserPermissions.js';
// Note: also update LoginMethod
export var LoginProviderType;
(function (LoginProviderType) {
    LoginProviderType["SSO"] = "SSO";
    LoginProviderType["Google"] = "Google";
})(LoginProviderType || (LoginProviderType = {}));
/**
 * Defines the applicable rate limits of the user (only used for API's).
 * Can only get changed by a platform admin
 */
export var ApiUserRateLimits;
(function (ApiUserRateLimits) {
    ApiUserRateLimits["Normal"] = "Normal";
    ApiUserRateLimits["Medium"] = "Medium";
    ApiUserRateLimits["High"] = "High";
})(ApiUserRateLimits || (ApiUserRateLimits = {}));
export class UserMeta extends AutoEncoder {
    /**
     * When signed in with SSO, this is the id of that user in the SSO system
     */
    loginProviderIds = new Map();
    rateLimits = null;
}
__decorate([
    field({ decoder: new MapDecoder(new EnumDecoder(LoginProviderType), StringDecoder) })
], UserMeta.prototype, "loginProviderIds", void 0);
__decorate([
    field({ decoder: new EnumDecoder(ApiUserRateLimits), nullable: true, version: 369 })
], UserMeta.prototype, "rateLimits", void 0);
export class User extends AutoEncoder {
    id;
    organizationId = null;
    memberId = null;
    firstName = null;
    lastName = null;
    email;
    permissions = null;
    /**
     * Readonly
     */
    meta = null;
    verified = false;
    /**
     * Readonly
     */
    hasAccount = false;
    /**
     * Readonly
     */
    hasPassword = false;
    /**
     * Readonly. The last time this user signed in or refreshed their session.
     */
    lastActiveAt = null;
    /**
     * Readonly. Whether this user has at least one second factor (an authenticator app or
     * a passkey) enrolled. The factors themselves are only visible to the user itself.
     */
    hasTwoFactor = false;
    get name() {
        if (!this.lastName) {
            if (!this.firstName) {
                return '';
            }
            return this.firstName;
        }
        return this.firstName + ' ' + this.lastName;
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], User.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 245 })
], User.prototype, "organizationId", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 281 })
], User.prototype, "memberId", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 14 })
], User.prototype, "firstName", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 14 })
], User.prototype, "lastName", void 0);
__decorate([
    field({ decoder: EmailDecoder })
], User.prototype, "email", void 0);
__decorate([
    field({ decoder: Permissions, nullable: true, version: 2, upgrade: () => null }),
    field({
        decoder: UserPermissions,
        nullable: true,
        version: 248,
        upgrade: function (oldValue) {
            if (!oldValue || !this.organizationId) {
                return null;
            }
            const m = new Map();
            m.set(this.organizationId, oldValue);
            return UserPermissions.create({
                globalPermissions: null,
                organizationPermissions: m,
            });
        },
    })
], User.prototype, "permissions", void 0);
__decorate([
    field({ decoder: UserMeta, nullable: true, version: 191 })
], User.prototype, "meta", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 81 })
], User.prototype, "verified", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 162 })
], User.prototype, "hasAccount", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 360 })
], User.prototype, "hasPassword", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true, version: 408 })
], User.prototype, "lastActiveAt", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 408 })
], User.prototype, "hasTwoFactor", void 0);
export class NewUser extends User {
    /**
     * Will be empty in responses for now
     */
    password = '';
}
__decorate([
    field({ decoder: StringDecoder, version: 162 })
], NewUser.prototype, "password", void 0);
export class ApiUser extends AutoEncoder {
    id;
    organizationId = null;
    name = null;
    permissions = null;
    /**
     * Readonly
     */
    meta = null;
    createdAt = new Date();
    expiresAt = null;
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], ApiUser.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 245 })
], ApiUser.prototype, "organizationId", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 14 })
], ApiUser.prototype, "name", void 0);
__decorate([
    field({ decoder: Permissions, nullable: true, version: 2, upgrade: () => null }),
    field({
        decoder: UserPermissions,
        nullable: true,
        version: 248,
        upgrade: function (oldValue) {
            if (!oldValue || !this.organizationId) {
                return null;
            }
            const m = new Map();
            m.set(this.organizationId, oldValue);
            return UserPermissions.create({
                globalPermissions: null,
                organizationPermissions: m,
            });
        },
    })
], ApiUser.prototype, "permissions", void 0);
__decorate([
    field({ decoder: UserMeta, nullable: true, version: 369 })
], ApiUser.prototype, "meta", void 0);
__decorate([
    field({ decoder: DateDecoder })
], ApiUser.prototype, "createdAt", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], ApiUser.prototype, "expiresAt", void 0);
export class ApiUserWithToken extends ApiUser {
    token;
}
__decorate([
    field({ decoder: StringDecoder })
], ApiUserWithToken.prototype, "token", void 0);
//# sourceMappingURL=User.js.map